import { baseApi } from '../Api';
import {
  META_GET_ALL,
  META_UPDATE,
  GET_ALL_WORK_SCHEDULES,
  SEARCH_WORK_SCHEDULES,
  GET_WORK_SCHEDULE_BY_ID,
  CREATE_WORK_SCHEDULE,
  UPDATE_WORK_SCHEDULE,
  DELETE_WORK_SCHEDULE,
  GET_ALL_DROPDOWN_WORK_SCHEDULE,
  BULK_DELETE_WORK_SCHEDULE,
  GET_WORK_SCHEDULE_BASIC_DETAILS,
} from './Endpoints';

export const workScheduleApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // ============================
    //  Work Schedule APIs
    // ============================

    // POST: Get all work schedules (with filters, sorting, pagination) — query variant
    getAllWorkSchedulesList: build.query({
      query: (body) => ({
        url: GET_ALL_WORK_SCHEDULES,
        method: 'POST',
        data: body,
      }),
    }),

    // POST: Get all work schedules (with filters, sorting, pagination)
    getAllWorkSchedules: build.mutation({
      query: (body) => ({
        url: GET_ALL_WORK_SCHEDULES,
        method: 'POST',
        data: body,
      }),
    }),

    // POST: Search work schedules
    searchWorkSchedules: build.mutation({
      query: ({ requestTypeCode, body }) => ({
        url: `${SEARCH_WORK_SCHEDULES}?requestTypeCode=${requestTypeCode}`,
        method: 'POST',
        data: body,
      }),
    }),

    // GET: Get single work schedule by ID
    getWorkScheduleById: build.query({
      query: (id) => ({
        url: `${GET_WORK_SCHEDULE_BY_ID}/${id}`,
        method: 'GET',
      }),
    }),

    // POST: Create new work schedule
    createWorkSchedule: build.mutation({
      query: (body) => ({
        url: CREATE_WORK_SCHEDULE,
        method: 'POST',
        data: body,
      }),
      transformResponse: (raw) => {
        const code = `${raw?.errCode ?? ''}`.trim();

        // 409 → duplicate → throw error
        if (code === '409') {
          const err = new Error(raw?.errDescription ?? 'Duplicate entry');
          err.name = 'DuplicateError';
          err.data = raw;
          throw err;
        }

        // 410 → dependency error → return raw so component can show table
        if (code === '410') return raw;

        // 000 → success
        if (code === '000') return raw;

        // Any other error code → throw
        if (code && code !== '000') {
          const err = new Error(raw?.errDescription ?? 'Something went wrong');
          err.data = raw;
          throw err;
        }

        return raw;
      },
    }),

    // PUT: Update existing work schedule
    updateWorkSchedule: build.mutation({
      query: ({ id, body }) => ({
        url: `${UPDATE_WORK_SCHEDULE}/${id}`,
        method: 'PUT',
        data: body,
      }),
      transformResponse: (raw) => {
        const code = `${raw?.errCode ?? ''}`.trim();

        // 410 → dependency error → return raw so component can show table
        if (code === '410') return raw;

        // 000 → success
        if (code === '000') return raw;

        // Any other error code → throw
        if (code && code !== '000') {
          const err = new Error(raw?.errDescription ?? 'Something went wrong');
          err.data = raw;
          throw err;
        }

        return raw;
      },
    }),

    // DELETE: Delete a work schedule
    deleteWorkSchedule: build.mutation({
      query: ({ id }) => ({
        url: `${DELETE_WORK_SCHEDULE}/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (raw) => {
        const code = `${raw?.errCode ?? ''}`.trim();

        // 410 → dependency error → return raw so component can show table
        if (code === '410') return raw;

        // 000 → success
        if (code === '000') return raw;

        // Any other error code → throw
        if (code && code !== '000') {
          const err = new Error(raw?.errDescription ?? 'Something went wrong');
          err.data = raw;
          throw err;
        }

        return raw;
      },
    }),

    // PUT: Bulk Delete work schedules
    bulkDeleteWorkSchedule: build.mutation({
      query: (body) => ({
        url: BULK_DELETE_WORK_SCHEDULE,
        method: 'DELETE',
        data: body, 
      }),
      transformResponse: (raw) => {
        const code = `${raw?.errCode ?? ''}`.trim();

        // 410 → dependency error → return raw so component can show table
        if (code === '410') return raw;

        // 000 → success
        if (code === '000') return raw;

        // Any other error code → throw
        if (code && code !== '000') {
          const err = new Error(raw?.errDescription ?? 'Something went wrong');
          err.data = raw;
          throw err;
        }

        return raw;
      },
    }),

    // GET: Get Work Schedule Basic Details
    getWorkScheduleBasicDetails: build.query({
      query: ({ companyId, workScheduleID }) => ({
        url: GET_WORK_SCHEDULE_BASIC_DETAILS,
        method: 'GET',
        params: { companyId, workScheduleID },
      }),
    }),

    // ============================
    // TimeManagementLookup
    // ============================
    getAllDropdownScheduleList: build.query({
      query: ({ lookupCode, body }) => ({
        url: `${GET_ALL_DROPDOWN_WORK_SCHEDULE}?lookupCode=${lookupCode}`,
        method: 'POST',
        data: body,
      }),
    }),
    getAllDropdownSchedule: build.mutation({
      query: ({ lookupCode, body }) => ({
        url: `${GET_ALL_DROPDOWN_WORK_SCHEDULE}?lookupCode=${lookupCode}`,
        method: 'POST',
        data: body,
      }),
    }),

    // ============================
    // META DATA APIs
    // ============================

    // GET: Get all meta-data
    getMetaData: build.query({
      query: ({ companyId, tableCode }) => ({
        url: META_GET_ALL,
        method: 'GET',
        params: { companyId, tableCode },
      }),
    }),

    // PUT: Update meta-data
    updateMetaData: build.mutation({
      query: (body) => ({
        url: META_UPDATE,
        method: 'PUT',
        data: body,
      }),
    }),
  }),
});

export const {
  // Meta
  useGetMetaDataQuery,
  useLazyGetMetaDataQuery,
  useUpdateMetaDataMutation,

  // Work Schedule
  useGetAllWorkSchedulesListQuery,
  useLazyGetAllWorkSchedulesListQuery,
  useGetAllWorkSchedulesMutation,
  useSearchWorkSchedulesMutation,
  useGetWorkScheduleByIdQuery,
  useLazyGetWorkScheduleByIdQuery,
  useCreateWorkScheduleMutation,
  useUpdateWorkScheduleMutation,
  useDeleteWorkScheduleMutation,
  useBulkDeleteWorkScheduleMutation,

  useGetWorkScheduleBasicDetailsQuery,
  useLazyGetWorkScheduleBasicDetailsQuery,

  // TimeManagementLookup
  useGetAllDropdownScheduleListQuery,
  useGetAllDropdownScheduleMutation,

  getAllDropdownSchedule,
} = workScheduleApi;
